import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { makeStyles } from "@material-ui/core/styles";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Grid,
  Typography,
} from "@material-ui/core";

import componentStyles from "assets/theme/views/auth/login";
import componentStylesButtons from "assets/theme/components/button";
import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";
import UtilsFncs from "utils/UtilsFncs";
import PasswordField from "components/_refactor/fields/password";

const useStyles = makeStyles(componentStyles);
const useStylesButtons = makeStyles(componentStylesButtons);

const MENSAGEM_LINK_INVALIDO =
  "Link de recuperação inválido ou expirado. Solicite uma nova recuperação de senha.";

function Alert({ text }) {
  return (
    <ReactBSAlert
      success
      style={{ display: "block", marginTop: "-100px" }}
      title="Senha alterada."
      onConfirm={UtilsFncs.RedirectToLoginPage}
      onCancel={UtilsFncs.RedirectToLoginPage}
      confirmBtnBsStyle="info"
      confirmBtnText="Ok"
      btnSize=""
      confirmBtnStyle={{
        marginRight: undefined,
        borderColor: undefined,
        boxShadow: undefined,
      }}
    >
      {text}
    </ReactBSAlert>
  );
}

function AlertError({ text, closeAlert }) {
  return (
    <ReactBSAlert
      error
      style={{ display: "block", marginTop: "-100px" }}
      title="Ops"
      onConfirm={closeAlert}
      onCancel={closeAlert}
      confirmBtnBsStyle="info"
      confirmBtnText="Ok"
      btnSize=""
      confirmBtnStyle={{
        marginRight: undefined,
        borderColor: undefined,
        boxShadow: undefined,
      }}
    >
      {text}
    </ReactBSAlert>
  );
}

// O e-mail chega em base64 na querystring do link enviado por e-mail (mesmo formato do activate-user).
function decodeEmail(emailBase64) {
  try {
    return Buffer.from(emailBase64, "base64").toString("ascii");
  } catch (error) {
    return "";
  }
}

export default function RecoverPassword() {
  const classes = { ...useStyles(), ...useStylesButtons() };
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [validatingLink, setValidatingLink] = useState(true);
  const [linkIsValid, setLinkIsValid] = useState(false);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmationPassword, setConfirmationPassword] = useState("");
  const [passwordIsOk, setPasswordIsOk] = useState(false);
  const [alert, setalert] = useState(null);

  const closeAlert = () => setalert(null);

  useEffect(() => {
    async function validateLink() {
      const params = new URLSearchParams(location.search);
      const tokenFromLink =
        params.get("token")?.replace(/^(?:')(.*)(?:')$/, "$1") || "";
      const emailFromLink = decodeEmail(params.get("email") || "");

      if (!tokenFromLink || !MaskAndValid.validateEmail(emailFromLink)) {
        setLinkIsValid(false);
        setValidatingLink(false);
        return;
      }

      setEmail(emailFromLink);
      setToken(tokenFromLink);

      try {
        const res = await Api.get(
          "recover-password",
          "login",
          "?email=" +
            encodeURIComponent(emailFromLink) +
            "&token=" +
            encodeURIComponent(tokenFromLink),
          false
        );

        setLinkIsValid(Boolean(res?.Success && res?.Result?.Valido));
      } catch (error) {
        console.log(error);
        setLinkIsValid(false);
      }

      setValidatingLink(false);
    }

    validateLink();
  }, [location.search]);

  async function submit() {
    setLoading(true);

    try {
      const res = await Api.post(
        "reset-password-by-token",
        "login",
        { email, token, senhaNova: newPassword },
        false
      );

      if (res?.Result?.Trocou) {
        setalert(<Alert text="Senha alterada com sucesso!" />);
        return;
      }

      setalert(
        <AlertError text={MENSAGEM_LINK_INVALIDO} closeAlert={closeAlert} />
      );
      setLoading(false);
    } catch (error) {
      console.log(error);
      setalert(<AlertError text={error} closeAlert={closeAlert} />);
      setLoading(false);
    }
  }

  const submitDisabled =
    loading ||
    !passwordIsOk ||
    newPassword !== confirmationPassword ||
    MaskAndValid.isNullOrEmpty(newPassword) ||
    MaskAndValid.isNullOrEmpty(confirmationPassword);

  if (validatingLink) {
    return (
      <Container maxWidth="sm">
        <Card classes={{ root: classes.card }}>
          <CardContent classes={{ root: classes.cardContent }}>
            <Typography variant="h5" color="textSecondary" align="center">
              Validando link de recuperação...
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!linkIsValid) {
    return (
      <Container maxWidth="sm">
        <Card classes={{ root: classes.card }}>
          <CardContent classes={{ root: classes.cardContent }}>
            <Typography variant="h5" color="textSecondary" align="center">
              {MENSAGEM_LINK_INVALIDO}
            </Typography>
            <Box textAlign="center" marginTop="1.5rem" marginBottom="1.5rem">
              <Button
                variant="contained"
                classes={{ root: classes.buttonContainedInfo }}
                onClick={UtilsFncs.RedirectToLoginPage}
              >
                Voltar para o login
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <>
      {alert}
      <Container maxWidth="sm">
        <Card classes={{ root: classes.card }}>
          <CardHeader
            className={classes.cardHeader}
            title="Preencha a nova senha de acordo com os critérios."
            titleTypographyProps={{
              component: Typography,
              variant: "h4",
              color: "textSecondary",
              align: "center",
            }}
          />
          <CardContent classes={{ root: classes.cardContent }}>
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <PasswordField
                    label="Senha"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    strengthInfo
                    white
                    required
                  />
                </Grid>
                <Grid className={classes.fields} item xs={12}>
                  <PasswordField
                    label="Confirmar Senha"
                    value={confirmationPassword}
                    onChange={(e) => setConfirmationPassword(e.target.value)}
                    white
                    confirmPassword={newPassword}
                    setPasswordIsOk={setPasswordIsOk}
                    required
                  />
                </Grid>
              </Grid>
              <Box textAlign="center" marginTop="1.5rem" marginBottom="1.5rem">
                <Button
                  variant="contained"
                  classes={{ root: classes.buttonContainedInfo }}
                  onClick={submit}
                  disabled={submitDisabled}
                >
                  {loading ? "Alterando..." : "Alterar"}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
